// components/ChatMessages.jsx
import React, { useEffect, useRef } from 'react';
import { MathJax } from 'better-react-mathjax';
import { MarkdownContent } from './MarkdownContent';
import { Robot, Tools, CheckCircleFill, HourglassSplit } from 'react-bootstrap-icons'; // Added Tools, CheckCircleFill, HourglassSplit
import '../assets/styles/ChatMessages.css';

function renderTutorNameWithStatus(enableTts, ttsState) {
  return (
    <span className="msg-name" style={{ color: 'var(--primary-dark)' }}>
      AI Maths Tutor
      {/* TTS status indicators commented out - remove these comments to restore TTS UI 
      {enableTts && ttsState.isLoading && (
        <span className="tts-indicator loading" style={{ marginLeft: '8px', fontSize: '0.8em', color: '#666' }}>
          Loading...
        </span>
      )}
      {enableTts && ttsState.isPlaying && (
        <span className="tts-indicator speaking" style={{ marginLeft: '8px', fontSize: '0.8em', color: '#4a9f5a' }}>
          Speaking
        </span>
      )}
      */}
    </span>
  );
}

export function ChatMessages({ messages, enableTts = false, ttsState = { isLoading: false, isPlaying: false } }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Filter out meta messages and tool result messages that should not be displayed to the user
  const visibleMessages = messages.filter(msg => !msg.meta && msg.role !== 'tool');


  const renderMessage = (msg, idx) => {
    // --- OpenAI Tool Statuses ---
    if (msg.role === 'assistant' && msg.status === 'thinking_tool') {
      return (
        <div key={msg.id || idx} className="message assistant ephemeral">
          <div className="msg-header">
            <span className="msg-icon"><Robot style={{ color: 'var(--primary-color)' }}/></span>
            {renderTutorNameWithStatus(enableTts, ttsState)}
          </div>
          <div className="message-content">
            <span className="thinking tool-thinking">
              <HourglassSplit size={15} style={{ marginRight: '8px', color: 'var(--primary-color)', verticalAlign: 'middle' }} />
              {msg.toolName ? `Thinking about using ${msg.toolName}` : 'Thinking about using a tool'}
              <span className="dot-animation">...</span>
            </span>
          </div>
        </div>
      );
    }
    // --- End OpenAI Tool Statuses ---

    // General 'thinking' status
    if (msg.role === 'assistant' && msg.status === 'thinking') {
      return (
        <div key={msg.id || idx} className="message assistant ephemeral">
          <div className="msg-header">
            <span className="msg-icon"><Robot style={{ color: 'var(--primary-color)' }}/></span>
            {renderTutorNameWithStatus(enableTts, ttsState)}
          </div>
          <div className="message-content">
            <span className="thinking">Thinking<span className="dot-animation">...</span></span>
          </div>
        </div>
      );
    }
    
    // --- Bedrock Specific Statuses (can be removed if purely OpenAI) ---
    if (msg.status === 'toolStart' || msg.status === 'executing') { // Bedrock versions
        return (
          <div key={msg.id || idx} className="message assistant ephemeral">
            <div className="msg-header">
              <span className="msg-icon"><Robot style={{ color: 'var(--primary-color)' }}/></span>
              {renderTutorNameWithStatus(enableTts, ttsState)}
            </div>
            <div className="message-content">
              {msg.friendlyMessage || `${msg.status === 'toolStart' ? 'Starting' : 'Executing'} tool: ${msg.toolName || ''}`}
            </div>
          </div>
        );
    }
    // --- End Bedrock Specific ---


    if (msg.role === 'assistant') {
      // Assistant message may have 'openai_tool_request' type if it included tool_calls
      // but we usually only display its 'content' part here.
      // The tool_calls themselves aren't directly rendered as chat bubbles.
      if (msg.messageType === 'image') {
        return (
        <div key={msg.id || idx} className="message assistant">
          <div className="message-content">
          <div className="msg-header">
              <span className="msg-icon"><Robot style={{ color: 'var(--primary-color)' }}/></span>
              {renderTutorNameWithStatus(enableTts, ttsState)}
            </div>
            {renderAssistantContent(msg)}
          </div>
        </div>
        );
      }
      // Drawing messages from assistant (usually responses to user drawings)
      if (msg.messageType === 'drawing') {
        return (
          <div key={msg.id || idx} className="message assistant drawing-message">
            <div className="msg-header">
              <span className="msg-icon"><Robot style={{ color: 'var(--primary-color)' }}/></span>
              {renderTutorNameWithStatus(enableTts, ttsState)}
            </div>
            <div className="message-content">
              {typeof msg.content === 'string' && msg.content && <MarkdownContent content={msg.content} />}
              {msg.imageInfo?.imageUrl && (
                <div className="drawing-reference">
                  <img 
                    src={msg.imageInfo.imageUrl} 
                    alt={msg.imageInfo.alt || 'Referenced sketch'} 
                    className="drawing-image"
                  />
                </div>
              )}
            </div>
          </div>
        );
      }
      // Standard assistant text message (or content part of a tool_call message)
      return (
        <div key={msg.id || idx} className="message assistant">
          <div className="message-content">
            <div className="msg-header">
              <span className="msg-icon"><Robot style={{ color: 'var(--primary-color)' }}/></span>
              {renderTutorNameWithStatus(enableTts, ttsState)}
            </div>
            {renderAssistantContent(msg)}
          </div>
        </div>
      );
    } else if (msg.role === 'user_attachment' || (msg.messageType === 'file' && msg.role === 'user')) {
      // Handle file uploads from users
      return (
        <div key={msg.id || idx} className="message user file-attachment">
          <div className="message-content">
            <div className="msg-header">
              {/* User name can be added if desired */}
            </div>
            <div className="file-upload-content">
              <div className="file-info">
                <div className="file-name">
                  {/* msg.content for user_attachment is often the "Uploaded file: ..." text */}
                  <span>{msg.content || `File: ${msg.fileName || "Uploaded file"}`}</span>
                </div>
              </div>
              {msg.imagePreviewUrl && (
                <div className="file-preview">
                  <img 
                    src={msg.imagePreviewUrl} 
                    alt={msg.fileName || "Uploaded image"} 
                    className="uploaded-image" 
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    } else if (msg.messageType === 'file' && msg.role === 'assistant') {
        // Assistant "file" messages (e.g., after processing a user upload in Bedrock style)
         return (
            <div key={msg.id || idx} className="message assistant file-attachment-response">
                <div className="msg-header">
                    <span className="msg-icon"><Robot style={{ color: 'var(--primary-color)' }}/></span>
                    {renderTutorNameWithStatus(enableTts, ttsState)}
                </div>
                <div className="message-content">
                    <MarkdownContent content={msg.content || `Processed file: ${msg.fileName}`} />
                    {/* No preview for assistant file responses unless explicitly structured */}
                </div>
            </div>
        );
    } else if (msg.role === 'user') {
      // User image messages (e.g. if user could paste images)
      if (msg.messageType === 'image' && msg.imageInfo?.imageUrl) {
        return (
          <div key={msg.id || idx} className="message user image-message">
            <div className="message-content">
              <div className="msg-header"><span className="msg-name">You</span></div>
              <div className="image-segment">
                <img src={msg.imageInfo.imageUrl} alt={msg.imageInfo.alt || 'User image'} className="user-image"/>
              </div>
              {typeof msg.content === 'string' && msg.content.trim() && <MarkdownContent content={msg.content} />}
            </div>
          </div>
        );
      }
      // User drawing messages
      if (msg.messageType === 'drawing' && msg.imageInfo?.imageUrl) {
        return (
          <div key={msg.id || idx} className="message user drawing-message">
            <div className="message-content">
              <div className="msg-header"><span className="msg-name">You</span></div>
              <div className="drawing-container">
                {typeof msg.content === 'string' && msg.content.trim() && <p className="drawing-caption">{msg.content}</p>}
                <img src={msg.imageInfo.imageUrl} alt={msg.imageInfo.alt || 'Student sketch'} className="user-drawing-image"/>
              </div>
            </div>
          </div>
        );
      }
      // Standard user text message
      return (
        <div key={msg.id || idx} className="message user">
          <div className="message-content">
            <div className="msg-header">{/* <span className="msg-name">You</span> Optional */}</div>
            {renderUserContent(msg)}
          </div>
        </div>
      );
    } else if (msg.role !== 'tool') { // Ensure 'tool' role doesn't fall into this 'system' or 'other'
      // System or other messages
      return (
        <div key={msg.id || idx} className="message system">
          <div className="message-content">
            <div className="msg-header"><span className="msg-icon">⚙️</span><span className="msg-name">System</span></div>
            {typeof msg.content === 'string' && <MarkdownContent content={msg.content} />}
          </div>
        </div>
      );
    }
    return null; // Fallback, should ideally not be reached if all types are handled
  };

  return (
    <div className="chat-messages-container" ref={scrollRef} style={{ overflowY: 'auto', flex: 1 }}>
      {visibleMessages.map((msg, idx) => renderMessage(msg, idx))}
    </div>
  );
}

function renderAssistantContent(msg) {
  // Handles msg.content which can be a string or an array of segments (text/image)
  if (Array.isArray(msg.content)) {
    return msg.content.map((segment, idx) => {
      if (segment.type === 'text') {
        return <MarkdownContent key={idx} content={segment.text || ''} />;
      } else if (segment.type === 'image' && segment.url) {
        return (
          <div key={idx} className="image-segment assistant-image-segment">
            <img 
              src={segment.url} 
              alt={segment.alt || 'Generated image'} 
              className="assistant-image"
            />
            {segment.caption && <div className="image-caption">{segment.caption}</div>}
          </div>
        );
      }
      // Fallback for unknown segment types in array
      return <MarkdownContent key={idx} content={JSON.stringify(segment)} />;
    });
  } else if (typeof msg.content === 'string') {
    return <MarkdownContent content={msg.content} />;
  } else if (msg.content === null && msg.tool_calls) {
    // If content is null but there are tool_calls, it means the text part was empty.
    // We might show a generic message or nothing, depending on preference.
    // return <em style={{color: "#777", fontSize: "0.9em"}}>[Assistant is using tools...]</em>;
    return null; // Often, the status message covers this.
  }
  // Fallback for other content types (e.g., object but not array)
  return msg.content ? <pre>{JSON.stringify(msg.content, null, 2)}</pre> : null;
}

function renderUserContent(msg) {
  // Handles msg.content which can be a string or an array for user messages (e.g., for future multi-modal input)
  if (Array.isArray(msg.content)) {
    return msg.content.map((segment, idx) => {
      if (segment.type === 'text') {
        return <MarkdownContent key={idx} content={segment.text || ''} />;
      } else if (segment.type === 'image_url' && segment.image_url?.url) { // Example for OpenAI vision format
        return (
          <div key={idx} className="image-segment user-image-segment">
            <img src={segment.image_url.url} alt={'User uploaded image'} style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: 8 }} />
          </div>
        );
      }
      // Fallback for unknown segment types
      return <MarkdownContent key={idx} content={JSON.stringify(segment)} />;
    });
  } else if (typeof msg.content === 'string') {
    const formattedContent = msg.content.replace(/\n/g, '  \n'); // Ensure markdown line breaks
    return <MarkdownContent content={formattedContent} />;
  }
  // Fallback for other content types
  return msg.content ? <pre>{JSON.stringify(msg.content, null, 2)}</pre> : null;
}